import KaboodleToaster from "components/common/KaboodleToaster";
import DocumentList from "components/Documents/table/DocumentList";
import { DatePicker } from "components/inputs";
import { format } from "date-fns";
import { useGetDocumentsQuery } from "services/documents-api";
import type { ConstantTypes, MSADataType } from "types/DataTypes";
import { STRING_TO_DATE } from "utils/date-utils";
import { useConstants, useQuery } from "utils/hooks";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

type FinalSettlementDocumentsProps = {
  service: MSADataType;
  onSave: (field: string, value: unknown) => Promise<void>;
};

const FinalSettlementDocuments = ({
  service,
  onSave,
}: FinalSettlementDocumentsProps) => {
  const { constants } = useConstants();
  const query = useQuery();
  const documentSearchTerm = query.get(GLOBAL_SEARCH_PARAM);

  // only show the final settlement document type
  const finalSettleDocType = constants?.DOCUMENT_TYPES?.find(
    (type: ConstantTypes["DOCUMENT_TYPES"][0]) =>
      type.value === "FINAL_SETTLEMENT_DOCUMENT",
  )?.value;

  const {
    data: documents = [],
    isLoading,
    isError,
  } = useGetDocumentsQuery({
    fileId: service.file.id.toString(),
    serviceId: service.id.toString(),
    serviceType: "msa",
    filters: finalSettleDocType ? { type: finalSettleDocType } : undefined,
    searchTerm: documentSearchTerm || undefined,
  });

  const handleDateReceivedChange = async (date: Date) => {
    try {
      await onSave(
        "final_settlement_documents_received_on",
        format(date, "yyyy-MM-dd"),
      );
    } catch (error) {
      console.error("Failed to update date received:", error);
      KaboodleToaster.error(error);
    }
  };

  const handleDateUploadedChange = async (date: Date) => {
    try {
      await onSave(
        "final_settlement_documents_uploaded_on",
        format(date, "yyyy-MM-dd"),
      );
    } catch (error) {
      console.error("Failed to update date uploaded:", error);
      KaboodleToaster.error(error);
    }
  };

  return (
    <div className="swim-column">
      <div className="data-block max-content-w">
        <div className="data-block-header">Final Settlement Documents</div>
        <div className="data-values">
          <label>F.S.D Uploaded to CMS on:</label>
          <DatePicker
            name="final-settlement-documents-uploaded-on"
            onDateChange={handleDateUploadedChange}
            initialDate={
              service.final_settlement_documents_uploaded_on
                ? STRING_TO_DATE(service.final_settlement_documents_uploaded_on)
                : undefined
            }
            allowFutureDates={true}
            isClearable={true}
          />

          <label>Date Received:</label>
          <DatePicker
            name="final-settlement-documents-received-on"
            onDateChange={handleDateReceivedChange}
            initialDate={
              service.final_settlement_documents_received_on
                ? STRING_TO_DATE(service.final_settlement_documents_received_on)
                : undefined
            }
            readOnly={
              !Array.isArray(documents)
                ? documents.results.length === 0
                : documents.length === 0
            }
          />

          <label>Documents:</label>
          <DocumentList
            documents={Array.isArray(documents) ? documents : documents.results}
            isLoading={isLoading}
            isError={isError}
          />
        </div>
      </div>
    </div>
  );
};

export default FinalSettlementDocuments;
